import { TileMeta, TileSize } from "game-common/models"
import { roughToFineCoordinates } from "game-common/util"

/**
 * Where CompositeTileRenderer draws a composite tile (a tree, an ore block, a boulder...) when it
 * draws it as ONE sprite, and how deep that sprite sorts against bodies.
 *
 * Pure and pixi-free, in its own module for the same reason as sprited_tile_zindex.ts: tools that
 * preview the map (Sprite Lab's Autotiles view) place composites with these exact functions instead
 * of a copy of them.
 */

/** The fields of a composite's parent tile that position and sort it. */
export interface CompositeParentMeta {
    tileMeta?: Pick<TileMeta, "blockingRects" | "renderXOffset" | "renderYOffset" | "renderZIdxOffset">
}

/** A composite's art rectangle, in cells: TileTextureExtractor.compositeTileSpriteBounds. */
export interface CompositeArtSize {
    width: number
    height: number
}

// Collision footprints and entity positions share the same coordinate system.
// Sorting at the trunk band lets a body at south contact draw in front of the
// roots, including when that band sits above the composite's parent tile.
export const compositeFootprintDepth = (compositeTileMeta: CompositeParentMeta | undefined, y: number): number => {
    const rects = compositeTileMeta?.tileMeta?.blockingRects
    if (!rects?.length) return undefined
    const top = Math.min(...rects.map(rect => rect.y))
    const bottom = Math.max(...rects.map(rect => rect.y + rect.height))
    return 100 + y * TileSize + (top + bottom) / 2
}

/**
 * The single sprite of a composite whose parent tile is at cell (x, y): the art hangs UP from the
 * parent's row (its bottom row is the parent's), centred-ish on it when it is wider than a cell, and
 * sorts at its footprint's trunk band, or three quarters of a cell above the rendered art's foot
 * without one. Authored footprints already include render offsets; the fallback must include them.
 */
export const compositeSpritePlacement = (
    compositeTileMeta: CompositeParentMeta | undefined,
    bounds: CompositeArtSize,
    x: number,
    y: number,
    extraZIndex?: number,
): { x: number; y: number; zIndex: number } => {
    const fine = roughToFineCoordinates({ x, y: y - bounds.height + 1 })
    return {
        x:
            fine.x -
            (bounds.width > 1 ? bounds.width * 0.25 * TileSize : 0) +
            (compositeTileMeta?.tileMeta?.renderXOffset || 0),
        y: fine.y + (compositeTileMeta?.tileMeta?.renderYOffset || 0),
        zIndex:
            (extraZIndex || 0) + (compositeTileMeta?.tileMeta?.renderZIdxOffset || 0) +
            (compositeFootprintDepth(compositeTileMeta, y) ??
                100 + fine.y + (compositeTileMeta?.tileMeta?.renderYOffset || 0) + bounds.height * TileSize - TileSize * 0.75),
    }
}
